/**
 * mensagens.ts — os tipos e as chamadas do MOTOR DE MENSAGENS (`/api/mensagens`).
 *
 * Um arquivo por assunto em `lib/`: quem pluga o componente não escreve URL.
 * Os tipos espelham `schema_mensagem.py` — o mesmo JSON no polling de hoje e
 * no SSE de amanhã.
 */
import { api } from '@/lib/api';

export type TipoAncora = 'ordens_servico' | 'autorizacoes' | (string & {});

export interface Ancora {
  tipo: TipoAncora;
  id: number;
  titulo?: string | null;
  link?: string;
}

export interface Pessoa {
  id: number;
  nome: string;
  foto?: string | null;
  papel?: string | null;
}

export interface Sala {
  id: number;
  titulo: string;
  visibilidade: 'herdada' | 'restrita';
  estado: 'aberta' | 'fechada' | 'arquivada';
  criacao: string | null;
  aberta_por: { id: number; nome: string };
  fechamento: string | null;
  ancoras: Ancora[];
  nao_lidas: number;
  /** quantas das não lidas CHAMAM você pelo nome — sinal diferente, marca diferente */
  mencoes_nao_lidas: number;
  ultima_mensagem: { id: number; trecho: string; autor_nome: string | null; criacao: string | null } | null;
  meu_cursor: number;
  /** até que mensagem todos os OUTROS participantes já leram — os dois tiques; null sem outros */
  lido_por_todos_ate: number | null;
  meu_papel: string | null;
  posso_falar: boolean;
  posso_gerenciar: boolean;
  participantes: Pessoa[];
}

export interface Anexo {
  id: number;
  tipo: 'foto' | 'audio' | 'video' | 'arquivo';
  url: string;
  nome?: string | null;
  tamanho_bytes?: number | null;
}

export interface Mensagem {
  id: number;
  sala_id: number;
  origem: 'pessoa' | 'sistema';
  autor: Pessoa | null;
  texto: string | null;
  responde: { id: number; autor_nome: string | null; trecho: string } | null;
  anexos: Anexo[];
  mencoes: { id: number; nome: string }[];
  criacao: string | null;
  /** DE ONDE a pessoa falou (095): a etapa da autorização, ou null. */
  etapa_id?: number | null;
}

const chaveAncora = (a: Ancora) => `${a.tipo}:${a.id}`;

export const mensagensApi = {
  salasDoItem: (ancora: Ancora) =>
    api.get<Sala[]>(`/mensagens/salas?ancora=${encodeURIComponent(chaveAncora(ancora))}`),
  minhasSalas: (soComNovas = false) =>
    api.get<Sala[]>(`/mensagens/salas?minhas=1${soComNovas ? '&com_novas=1' : ''}`),
  abrirSala: (dados: {
    titulo: string;
    visibilidade: 'herdada' | 'restrita';
    ancoras: { tipo: TipoAncora; id: number }[];
    participantes?: { usuario_id: number; papel?: 'le' | 'fala' | 'gerencia' }[];
  }) => api.post<Sala>('/mensagens/salas', dados),
  sala: (id: number) => api.get<Sala>(`/mensagens/salas/${id}`),
  fechar: (id: number) => api.post<Sala>(`/mensagens/salas/${id}/fechar`),
  reabrir: (id: number) => api.post<Sala>(`/mensagens/salas/${id}/reabrir`),
  arquivar: (id: number) => api.post<Sala>(`/mensagens/salas/${id}/arquivar`),
  estender: (id: number, ancora: { tipo: TipoAncora; id: number }) =>
    api.post<Sala>(`/mensagens/salas/${id}/ancoras`, ancora),
  mencionaveis: (id: number) => api.get<Pessoa[]>(`/mensagens/salas/${id}/participantes?mencionaveis=1`),
  adicionar: (id: number, usuario_id: number, papel: 'le' | 'fala' | 'gerencia' = 'fala') =>
    api.post<Sala>(`/mensagens/salas/${id}/participantes`, { usuario_id, papel }),
  remover: (id: number, usuario_id: number) =>
    api.delete<Sala>(`/mensagens/salas/${id}/participantes/${usuario_id}`),
  mensagens: (id: number, depoisDe = 0) =>
    api.get<Mensagem[]>(`/mensagens/salas/${id}/mensagens?depois_de=${depoisDe}`),
  enviar: (id: number, dados: { texto?: string; responde_id?: number; anexos?: number[]; mencoes?: number[]; etapa_id?: number }) =>
    api.post<Mensagem>(`/mensagens/salas/${id}/mensagens`, dados),
  removerMensagem: (id: number, mensagemId: number) =>
    api.delete<void>(`/mensagens/salas/${id}/mensagens/${mensagemId}`),
  marcarLido: (id: number, ateId: number) =>
    api.post<{ ultimo_lido: number }>(`/mensagens/salas/${id}/lido`, { ate_id: ateId }),
  subirAnexo: (arquivo: File) => {
    const fd = new FormData();
    fd.append('arquivo', arquivo);
    return api.upload<Anexo>('/mensagens/anexos', fd);
  },
};
